import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Spinner from "../../components/BootstrapCompo";
import BreadCrumb from "../../components/common/BreadCrumb";
import DownloadFileModal from "../../components/common/download-file-modal";
import PaginationLayout from "../../components/PaginationLayout";
import { GlobalContext, handleError } from "../../context/Provider";
import henceforthApi from "../../utils/henceforthApi";
import NODATA from '../../assets/images/no-data-found.svg';
import { INSUFFICIENT_PERMISSIONS } from "../../context/actionTypes";

interface DocumentType {
    _id: string;
    resource: string;
    name: string;
    description: string | null;
    status: "ACTIVE" | "INACTIVE";
    sides_required: "FRONT" | "BACK" | "BOTH";
    createdAt: number;
    updatedAt: number;
}

const DocumentTypeListing = () => {
    const breadCrumbPath = [
        { name: 'Home', url: '/', active: '' },
        { name: 'Document Types', url: '', active: 'not-allowed' }
    ];

    const { loading, setLoading, onChangePagination } = useContext(GlobalContext);
    const { authState } = useContext(GlobalContext);
    const navigate = useNavigate();
    const location = useLocation();
    const match = useMatch('/document-types/:page');
    const limit = 10;
    const newParam = new URLSearchParams(location.search);

    const [state, setState] = useState<{ total_count: number; data: DocumentType[] }>({
        total_count: 0,
        data: []
    });

    const initialise = async () => {
        setLoading(true);
        try {
            const apiRes = await henceforthApi.DocumentType.get(
                Number(match?.params.page) - 1,
                limit,
                newParam.toString()
            );
            setState(apiRes.data);
        } catch (error: any) {
            if (error?.response?.body?.error === INSUFFICIENT_PERMISSIONS) {
                window.history.back();
            }
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (name: string, value: any) => {
        if (value) {
            newParam.set(name, value);
        } else {
            if (newParam.has(name)) newParam.delete(name);
        }
        navigate({ search: newParam.toString() });
    };

    const handleDelete = async (id: string, name: string) => {
        if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
        setLoading(true);
        try {
            await henceforthApi.DocumentType.delete(id);
            toast.success('Document type deleted successfully');
            initialise(); // refresh list
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        initialise();
    }, [match?.params.page, newParam.get("search"), newParam.get("status"), authState?.lang]);

    return (
        <>
            <BreadCrumb pathNameDeclare={breadCrumbPath} />

            <div className='page-spacing'>
                <section className='product-listing'>
                    <div className="container-fluid">
                        {/* Search & Filters */}
                        <div className='common-card mb-4 border-0 card-spacing'>
                            <div className="row justify-content-between gy-3">
                                <div className="col-md-7">
                                    <div className="row">
                                        <div className="col-5">
                                            <div className='form-fields-box'>
                                                <label className='mb-1 form-label fw-semibold'>Search</label>
                                                <div className='position-relative'>
                                                    <input
                                                        type="search"
                                                        className="form-control rounded-0 ps-4"
                                                        name='search'
                                                        placeholder="Search by name..."
                                                        value={newParam.get('search') || ""}
                                                        onChange={(e) => handleSearch(e.target.name, e.target.value)}
                                                    />
                                                    <span className='search-icon'><i className='fa fa-search'></i></span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className='form-fields-box'>
                                                <label className='mb-1 form-label fw-semibold'>Status</label>
                                                <select
                                                    className="form-select rounded-0"
                                                    value={newParam.get('status') || ''}
                                                    onChange={(e) => handleSearch('status', e.target.value)}
                                                >
                                                    <option value="">All</option>
                                                    <option value="ACTIVE">Active</option>
                                                    <option value="INACTIVE">Inactive</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <div className='d-flex gap-3 justify-content-md-end'>
                                        <div className='download-export-box'>
                                            <label className='mb-1 form-label fw-semibold'>Add</label>
                                            <div className="export-button">
                                                <Link to="/document-types/add" className="btn btn-white">
                                                    <i className='fa fa-plus me-2'></i>Add
                                                </Link>
                                            </div>
                                        </div>
                                        {/* <div className='download-export-box'>
                                            <label className='mb-1 form-label fw-semibold'>Export</label>
                                            <div className="export-button">
                                                <button className="btn btn-white" type="button" data-bs-toggle="modal" data-bs-target="#fileDownloadModal">
                                                    <i className='fa fa-cloud-download me-2'></i>.csv
                                                </button>
                                            </div>
                                        </div> */}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className='vh-100 d-flex justify-content-center py-5'><Spinner /></div>
                        ) : (
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="common-card">
                                        <div className="common-card-title">
                                            <h5>Document Types</h5>
                                        </div>
                                        <div className="common-card-content">
                                            <div className='data-list-table table-responsive mb-3'>
                                                <table className="table table-striped align-middle">
                                                    <thead>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Name</th>
                                                            <th>Resource</th>
                                                            <th>Status</th>
                                                            <th>Sides Required</th>
                                                            <th>Created At</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {state.data.length ? (
                                                            state.data.map((res, index) => (
                                                                <tr key={res._id}>
                                                                    <td>{index + 1}</td>
                                                                    <td>{res.name}</td>
                                                                    <td>{res.resource}</td>
                                                                    <td>
                                                                        <span className={`badge ${res.status === 'ACTIVE' ? 'bg-success' : 'bg-secondary'}`}>
                                                                            {res.status}
                                                                        </span>
                                                                    </td>
                                                                    <td>{res.sides_required}</td>
                                                                    <td>{moment(res.createdAt).format("DD/MM/YYYY")}</td>
                                                                    <td>
                                                                        <div className="btn-group gap-2">
                                                                            <Link to={`/document-types/${res._id}/edit`} className="btn btn-white btn-sm">
                                                                                <i className='fa fa-pencil me-1'></i>Edit
                                                                            </Link>
                                                                            <button
                                                                                className="btn btn-danger btn-sm"
                                                                                onClick={() => handleDelete(res._id, res.name)}
                                                                            >
                                                                                <i className='fa fa-trash me-1'></i>Delete
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={7} className="text-center py-3">
                                                                    <img src={NODATA} width="100" alt="No data" />
                                                                    <p className='text-center mt-3'>No document types found</p>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className='dashboad-pagination-box'>
                                                <PaginationLayout
                                                    count={state.total_count}
                                                    data={state.data}
                                                    page={Number(match?.params.page)}
                                                    limit={limit}
                                                    onPageChange={(val: number) => onChangePagination(val)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            {/* <DownloadFileModal exportData={exportData} /> */}
        </>
    );
};

export default DocumentTypeListing;